import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useMemo } from 'react';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => {
    return articles.find((a) => a.id === id);
  }, [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles.filter((a) => article.relatedArticles?.includes(a.id));
  }, [article]);

  if (!article) {
    return (
      <div className="pt-16">
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-500 text-sm mb-4">文章未找到</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryLabel =
    article.category === 'football' ? '足球' :
    article.category === 'basketball' ? '篮球' :
    article.category === 'tennis' ? '网球' :
    article.category === 'esports' ? '电竞' :
    article.category === 'promotions' ? '优惠活动' : '投注指南';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: 'bb体育',
    },
  };

  return (
    <div className="pt-16">
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: categoryLabel, href: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <article className="w-full px-4 md:px-6 py-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <span className="inline-block px-2.5 py-0.5 bg-primary-100 text-primary-700 text-xs rounded mb-3">
              {categoryLabel}
            </span>
            <h1 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
              </span>
            </div>
          </div>

          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - bb体育`}
              className="w-full h-auto object-cover"
            />
          </div>

          <div
            className="prose prose-sm max-w-none mb-8 text-foreground-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex items-center gap-2 mb-8 flex-wrap">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full border border-background-200/70"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="border-t border-background-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-heading font-semibold text-foreground-900">相关文章</h3>
              <Link to="/" className="text-xs text-primary-500 hover:text-primary-600 whitespace-nowrap">
                更多资讯 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.id}`}
                  className="bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all group"
                >
                  <div className="h-36 overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="text-xs font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-400 mt-1">{ra.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}